import { createApi } from '@reduxjs/toolkit/query/react'
import {
  CreateContractRequest,
  CreateEmploymentChangeLetterRequest,
  CreateTermSheetRequest,
} from '@/features/document-autofill/document-autofill.def.ts'
import { downloadDocx, downloadExcel, downloadFile, getFormattedTimestamp } from '@/utils/misc.ts'
import { toast } from '@/common/components/ui/use-toast.ts'
import { HcError } from '@/common/types/api.def.ts'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'

export const documentAutofillApi = createApi({
  reducerPath: 'documentAutofillApi',
  baseQuery: hcPortalBaseQuery(),
  endpoints: builder => ({
    createContract: builder.mutation<void, CreateContractRequest>({
      query: (request) => ({
        url: '/DocumentAutofill/CreateContract',
        method: 'POST',
        body: request,
        cache: 'no-cache',
        responseHandler: async (response) => {
          if (!response.ok) {
            const error = await response.json() as HcError
            toast({ title: 'Oops, something went wrong', description: error.message, variant: 'destructive' })
            return error
          }
          const blob = await response.blob()
          const timestamp = getFormattedTimestamp()
          downloadDocx(
            `${request.documentType === 'Employment' ? 'Employment ' : ''}Contract_${request.region}_${request.type}_${request.employeeName}_${timestamp}`,
            blob,
          )
        },
      }),
    }),
    createTermSheet: builder.mutation<void, CreateTermSheetRequest>({
      query: (request) => ({
        url: '/DocumentAutofill/CreateTermSheet',
        method: 'POST',
        body: {
          ...request,
          payload: JSON.stringify(request.payload),
        },
        cache: 'no-cache',
        responseHandler: async (response) => {
          if (!response.ok) {
            const error = await response.json() as HcError
            toast({ title: 'Oops, something went wrong', description: error.message, variant: 'destructive' })
            return error
          }
          const blob = await response.blob()
          const { fullName } = request.payload.personalInfo
          const timestamp = getFormattedTimestamp()
          downloadDocx(`PM Term Sheet - ${fullName} - ${timestamp}`, blob)
        },
      }),
    }),
    createEmploymentChangeLetter: builder.mutation<void, CreateEmploymentChangeLetterRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('excelFile', request.excelFile)

        return {
          url: '/EmploymentChange/CreateEmploymentChangeTwContracts',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
          responseHandler: async (response) => {
            if (!response.ok) {
              const error = await response.json() as HcError
              toast({ title: 'Oops, something went wrong', description: error.message, variant: 'destructive' })
              return error
            }

            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(`employment_change_contracts.zip`, blob)
          },
        }
      },
    }),
    getExcelEmploymentChangeTemplate: builder.mutation<void, void>({
      query: () => ({
        url: '/EmploymentChange/GetExcelImportTemplate',
        method: 'GET',
        cache: 'no-cache',
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`excel_import_template`, blob)
        },
      }),
    }),
  }),
})

export const {
  useCreateContractMutation,
  useCreateTermSheetMutation,
  useCreateEmploymentChangeLetterMutation,
  useGetExcelEmploymentChangeTemplateMutation,
} = documentAutofillApi
